import * as React from "react";
import { CalendarDays } from "lucide-react";

import { cn } from "@lab/src/lib/util";
import { Button } from "./button";
import { Calendar } from "./calendar";
import { Input } from "./input";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

const MONTHS_SHORT_DE = [
	"Jan",
	"Feb",
	"Mär",
	"Apr",
	"Mai",
	"Jun",
	"Jul",
	"Aug",
	"Sep",
	"Okt",
	"Nov",
	"Dez",
];

function formatDateTime(date: Date): string {
	const day = String(date.getDate()).padStart(2, "0");
	const month = MONTHS_SHORT_DE[date.getMonth()];
	const year = date.getFullYear();
	const hours = String(date.getHours()).padStart(2, "0");
	const minutes = String(date.getMinutes()).padStart(2, "0");
	return `${day}. ${month} ${year}, ${hours}:${minutes}`;
}

export type DateTimeDensitySize = "sm" | "md" | "lg";

interface DateTimePickerProps {
	value: Date | undefined;
	onChange: (date: Date | undefined) => void;
	placeholder?: string;
	className?: string;
	"data-testid"?: string;
	"aria-label"?: string;
	densitySize?: DateTimeDensitySize;
	inputHeight?: number;
}

export const DateTimePicker: React.FC<DateTimePickerProps> = ({
	value,
	onChange,
	placeholder = "Datum & Uhrzeit wählen",
	className,
	inputHeight,
	...props
}) => {
	const [open, setOpen] = React.useState(false);

	const timeValue = value
		? `${String(value.getHours()).padStart(2, "0")}:${String(
				value.getMinutes()
			).padStart(2, "0")}`
		: "12:00";

	const handleDateSelect = (day: Date | undefined) => {
		if (!day) {
			onChange(undefined);
			return;
		}
		const hours = value?.getHours() ?? 12;
		const minutes = value?.getMinutes() ?? 0;
		const result = new Date(day);
		result.setHours(hours, minutes, 0, 0);
		onChange(result);
	};

	const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const [hours, minutes] = e.target.value.split(":").map(Number);
		if (isNaN(hours) || isNaN(minutes)) return;
		const base = value ? new Date(value) : new Date();
		base.setHours(hours, minutes, 0, 0);
		onChange(base);
	};

	const handleClear = () => {
		onChange(undefined);
		setOpen(false);
	};

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					className={cn(
						"w-full justify-start text-left font-normal",
						!value && "text-muted-foreground",
						className
					)}
					style={
						inputHeight ? { height: `${inputHeight}px` } : undefined
					}
					data-testid={props["data-testid"]}
					aria-label={props["aria-label"]}
				>
					{value ? formatDateTime(value) : placeholder}
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-auto p-0" align="start">
				<Calendar
					selected={value}
					onSelect={handleDateSelect}
					defaultMonth={value}
				/>
				<div className="border-t px-3 py-3 flex items-center gap-2">
					<span className="text-sm text-muted-foreground whitespace-nowrap">
						Uhrzeit
					</span>
					<Input
						type="time"
						value={timeValue}
						onChange={handleTimeChange}
						className="h-8 text-sm"
						data-testid={
							props["data-testid"]
								? `${props["data-testid"]}-time`
								: undefined
						}
					/>
					{value && (
						<Button
							variant="ghost"
							size="sm"
							className="h-8 px-2 text-xs text-muted-foreground"
							onClick={handleClear}
						>
							Löschen
						</Button>
					)}
				</div>
			</PopoverContent>
		</Popover>
	);
};
